import React from 'react';
import { faCheckCircle, faScrewdriver } from '@fortawesome/free-solid-svg-icons';
import { FormGroup, Input } from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BaseFormViewModel } from '../common/ViewModel';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    FormLabel, onFieldChange, onReactSelectChanged, ToastMessage, ValidationErrorMessage, FormCheckbox,
} from '../common/forms/FormElements';
import { EquipmentType } from './Equipment';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { util } from '../Util';
import { handleFormSaveError } from '../common/forms/ValidationError';

export class EquipmentTypeForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            equipmentType: this.props.equipmentType ?? new EquipmentType(),
            groups: [],
            locations: [],
            show: false,
            statuses: [],
            requiredEquipment: [],
            afadTypes: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    onChange = onFieldChange.bind(this);

    onClose(response) {
        this.setState({ show: false });
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onDelete = async (e) => {
        const { equipmentType } = this.state;
        const response = await util.fetch.delete(ApiRoutes.equipmentType.delete(equipmentType.id));
        if (response) this.onClose(response);
    };

    onSelectChange = onReactSelectChanged.bind(this);

    onSubmit = async (e) => {
        const { equipmentType } = this.state;

        // Clear any fluent api errors
        this.setState({ errors: {}, saving: true });

        // Is this POST or PUT?
        const url = equipmentType.id
            ? ApiRoutes.equipmentType.update(equipmentType.id)
            : ApiRoutes.equipmentType.create();
        const fetch_cte = equipmentType.id ? util.fetch.put : util.fetch.post;

        const response = await fetch_cte(url, equipmentType).catch(this.handleSaveError);

        if (response) {
            toast.success(<ToastMessage
                icon={faCheckCircle}
                header="Save Successful"
                message={`Equipment Type [${equipmentType.description}] saved.`}
            />);
            this.setState({ show: false, saving: false });
            this.context.setFormOpened(false);
            this.resetForm();
            this.context.setFormOpened(false);
            this.props.onSaveCallback(response);
        }
    };

    handleSaveError = handleFormSaveError.bind(this);

    open = async (id) => {
        this.resetForm();
        this.setState({ show: true, loading: true });
        this.context.setFormOpened(true);

        let [equipmentType, requiredEquipmentOptions] = await Promise.all([
            (id ? util.fetch.js(ApiRoutes.equipmentType.byId(id)) : new EquipmentType()),
            util.fetch.js(ApiRoutes.equipmentType.all()),
        ]);

        if (equipmentType.id) {
            // Cannot assign dependent type as itself
            requiredEquipmentOptions = (requiredEquipmentOptions ?? [])
                .filter((x) => x.id !== equipmentType.id);
        }

        requiredEquipmentOptions = requiredEquipmentOptions.map((x) => ({ value: x.id, label: x.description }));

        this.setState({
            equipmentType,
            requiredEquipment: requiredEquipmentOptions,
            substituteEquipment: requiredEquipmentOptions,
            loading: false,
        });
    };

    async populateState() {
        const [groups] = await Promise.all([
            util.fetch.js(ApiRoutes.typeAheads.employeeGroups()),
            //util.fetch.js(ApiRoutes.typeAheads.equipmentTypeAFADTypes()),
        ]);

        const statuses = [{ 'value': true, 'label': 'Active' }, { 'value': false, 'label': 'Inactive' }]

        this.setState((state) => ({
            loading: false,
            groups,
            statuses
        }));
    }

    resetForm() {
        this.setState({ formValidated: false });
    }

    render() {
        const {
            requiredEquipment, substituteEquipment, groups, formValidated, validationMessage, statuses
        } = this.state;
        const eq = this.state.equipmentType;
        return (
            <SlideForm
                loading={this.state.loading}
                show={this.state.show}
                id="equipmentTypeForm"
                formIcon={faScrewdriver}
                formTitle={!!parseInt(eq.id ?? 0) > 0 ? 'Edit Equipment Type' : 'Add Equipment Type'}
                ref={this.formRef}
                setIsValidated={(value) => { this.setState({ formValidated: value }); }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onSave={this.onSave}
                onDelete={this.onDelete}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                validationMessage={validationMessage}
            >

                <FormGroup>
                    <FormLabel
                        htmlFor="description"
                        text="Description"
                        required
                    />
                    <Input
                        id="description"
                        name="equipmentType.description"
                        value={eq.description ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter description"
                        type="text"
                        required
                    />
                    <ValidationErrorMessage>Description is required.</ValidationErrorMessage>
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        id="groupsLabel"
                        htmlFor="groups"
                        text="Required User Groups"
                        helpMessage="Select any user groups that are required for this type of equipment."
                        required={!(eq.requiredEquipment ?? []).length}
                    />
                    <ValidatedSelect
                        id="groups"
                        name="equipmentType.groups"
                        isMulti
                        options={groups}
                        value={(groups ?? []).filter((x) => (eq.groups ?? []).includes(x.value)) ?? ''}
                        onChange={this.onSelectChange}
                        validationMessage="At least one is required: required user group or required equipment."
                        required={!(eq.requiredEquipment ?? []).length}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="status" text="Status" />
                    <Select
                        id="active"
                        name="equipmentType.active"
                        required
                        options={statuses}
                        value={
                            (statuses ?? []).find(
                                (x) => x.value === eq.active,
                            ) ?? ''
                        }
                        onChange={this.onSelectChange}
                    />
                    <ValidationErrorMessage>
                        Please select an active status
                    </ValidationErrorMessage>
                </FormGroup>

                {/*<FormGroup>*/}
                {/*    <FormLabel*/}
                {/*        id="employeeAssignableQuantityLabel"*/}
                {/*        htmlFor="employeeAssignableQuantity"*/}
                {/*        text="Employee Assignable Quantity"*/}
                {/*        required={!!(eq.groups ?? []).length}*/}
                {/*        helpMessage="Enter the amount that can be assigned to any of the selected user groups."*/}
                {/*    />*/}
                {/*    <Input*/}
                {/*        id="employeeAssignableQuantity"*/}
                {/*        name="equipmentType.employeeAssignableQuantity"*/}
                {/*        disabled={!(eq.groups ?? []).length}*/}
                {/*        value={eq.employeeAssignableQuantity ?? ''}*/}
                {/*        onChange={this.onChange}*/}
                {/*        placeholder="Enter Quantity"*/}
                {/*        type="number"*/}
                {/*        step="1"*/}
                {/*        required={!!(eq.groups ?? []).length}*/}
                {/*    />*/}
                {/*    <ValidationErrorMessage>This quantity is required.</ValidationErrorMessage>*/}
                {/*</FormGroup>*/}

                <FormGroup>
                    <FormLabel
                        id="requiredEquipmentLabel"
                        htmlFor="requiredEquipment"
                        text="Required Equipment"
                        required={!(eq.groups ?? []).length}
                        helpMessage="Select other equipment types that will be required in addition to this equipment when it is assigned."
                    />
                    <Select
                        id="requiredEquipment"
                        name="equipmentType.requiredEquipment"
                        isMulti
                        options={requiredEquipment}
                        value={(requiredEquipment ?? []).filter((x) => (eq.requiredEquipment ?? []).includes(x.value)) ?? ''}
                        onChange={this.onSelectChange}
                        required={!(eq.groups ?? []).length}
                        validationMessage="At least one is required: required user group or required equipment."
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        id="substituteEquipmentLabel"
                        htmlFor="substituteEquipment"
                        text="Substitute Equipment"
                        helpMessage="Select other equipment that can be substituted if this equipment is not available."
                    />
                    <Select
                        id="substituteEquipment"
                        name="equipmentType.substituteEquipment"
                        isMulti
                        options={substituteEquipment}
                        value={(substituteEquipment ?? []).filter((x) => (eq.substituteEquipment ?? []).includes(x.value)) ?? ''}
                        onChange={this.onSelectChange}
                    />
                </FormGroup>

                <FormGroup>
                    <FormCheckbox
                        id="isDriverRequired"
                        name="equipmentType.isDriverRequired"
                        checked={
                            eq.isDriverRequired ?? ''
                        }
                        onChange={this.onChange}
                        labelText="Is Driver Required"
                    />
                </FormGroup>
              
            </SlideForm>
        );
    }
}
